import React from "react";
import { Search, Plus, TrendingUp, MapPin } from "lucide-react";

const EmptyState = ({ type = "initial", onAction, selectedCity }) => {
    const isInitial = type === "initial";

    return (
        <div className="relative overflow-hidden bg-white/80 backdrop-blur-xl border border-white/40 shadow-[0_8px_32px_rgba(31,38,135,0.04)] rounded-[2.5rem] p-16 md:p-20 flex flex-col items-center text-center animate-in fade-in zoom-in duration-700 w-full mx-auto group">
            {/* Background Decorative Elements */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden rounded-[2.5rem] pointer-events-none">
                <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] bg-gradient-to-br from-blue-100/40 to-purple-100/40 rounded-full blur-3xl group-hover:scale-110 transition-transform duration-1000"></div>
                <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] bg-gradient-to-tr from-emerald-100/40 to-teal-100/40 rounded-full blur-3xl group-hover:scale-110 transition-transform duration-1000 delay-100"></div>
            </div>

            {/* Icon Container with Glassmorphism */}
            <div className="relative mb-10 z-10 transition-transform duration-500 group-hover:-translate-y-2">
                <div className={`absolute inset-0 rounded-[2rem] blur-xl opacity-50 ${isInitial ? 'bg-blue-400' : 'bg-slate-400'}`}></div>
                <div className={`relative w-24 h-24 rounded-[2rem] flex items-center justify-center backdrop-blur-md border border-white/60 shadow-xl ${isInitial
                        ? 'bg-gradient-to-br from-blue-50/90 to-blue-100/90 text-blue-600'
                        : 'bg-gradient-to-br from-slate-50/90 to-slate-100/90 text-slate-700'
                    }`}>
                    {isInitial ? (
                        <TrendingUp className="w-12 h-12 stroke-[1.5]" />
                    ) : (
                        <Search className="w-12 h-12 stroke-[1.5]" />
                    )}
                </div>
            </div>

            {/* High-Standard Typography & Highlighting */}
            <div className="max-w-xl relative z-10">
                <h3 className="text-3xl font-black text-slate-900 mb-5 tracking-tight flex flex-col items-center gap-4">
                    {isInitial ? (
                        "Property Portfolio Empty"
                    ) : (
                        <>
                            <span>No Results Found</span>
                            {selectedCity && selectedCity !== 'All Cities' && (
                                <div className="flex items-center gap-2 px-5 py-2 bg-slate-900 text-white rounded-full text-lg mt-1 shadow-lg shadow-slate-900/20 transform transition-all duration-300 hover:scale-105">
                                    <MapPin className="w-4 h-4 text-emerald-400" />
                                    <span className="font-bold tracking-wide">{selectedCity}</span>
                                </div>
                            )}
                        </>
                    )}
                </h3>

                <p className="text-slate-500 text-[15px] md:text-[17px] font-medium leading-relaxed mb-10">
                    {isInitial
                        ? "Your analyzed properties will appear here. Start by creating your first development profile to uncover insights."
                        : (
                            <>
                                We couldn't find any analyzed assets matching your criteria{selectedCity && selectedCity !== 'All Cities' ? ` in ` : '.'}
                                {selectedCity && selectedCity !== 'All Cities' && (
                                    <span className="font-bold text-slate-800">
                                        {selectedCity}
                                    </span>
                                )}
                                {selectedCity && selectedCity !== 'All Cities' && '. '}
                                Try adjusting your filters or start a new analysis to expand your portfolio.
                            </>
                        )
                    }
                </p>

                {/* Optional Action Button if onAction is provided */}
                {onAction && (
                    <button
                        onClick={onAction}
                        className="inline-flex items-center gap-2 px-7 py-3.5 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-2xl transition-all duration-300 shadow-md hover:shadow-xl hover:-translate-y-1 active:translate-y-0"
                    >
                        <Plus className="w-5 h-5" />
                        Start New Analysis
                    </button>
                )}
            </div>
        </div>
    );
};

export default EmptyState;
